/**
 * LiveSessionTV — Phase 5
 *
 * Full-screen shared display for live in-room sessions.
 * Route: /prioritization/sessions/:sessionId/tv
 *
 * State machine driven by session_events (Realtime):
 *   Default (collecting) → shows submission progress, avatar grid, live count
 *   reveal_started        → countdown overlay
 *   reveal_next_rank      → items cascade in, facilitator pacing
 *   reveal_complete       → all items visible, celebration
 *
 * Designed for 16:9 display at the front of a physical room.
 * No chrome — just the data.
 */

import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePresenceStore } from '../../../stores/prioritization/presenceStore';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { formatRank } from '@/lib/itemRankings';
import { roomIsAllIn } from '@/lib/liveSessionDisplay';
import { JoinQr } from '@/components/prioritization/session/JoinQr';
import { joinPathFor } from '@/lib/sessions/runItLive';
import { useSessionReveal } from '@/hooks/useSessionReveal';
import { namesHidden, useRoomTimer, useSessionRoomRules } from '@/lib/sessions/roomRules';
import { RoomCountdown } from '@/components/prioritization/session/RoomCountdown';
import { readTies, sharedRanks } from '@/lib/sessions/rankTies';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';

type TVState = 'collecting' | 'countdown' | 'revealing' | 'complete';

interface RevealItem {
  rank: number;
  title: string;
  score: number;
}

function initials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

export default function LiveSessionTV() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? 'TV';

  const sessions = useSessionStore(s => s.sessions);
  const fetchSessionById = useSessionStore(s => s.fetchSessionById);
  const joinPresence = usePresenceStore(s => s.join);
  const leavePresence = usePresenceStore(s => s.leave);
  const presentUsers = usePresenceStore(s => s.presentUsers);
  const participantResponses = useToolSessionStore(s => s.participantResponses);
  const toolSessions = useToolSessionStore(s => s.toolSessions);
  const subscribeToSessionToolSessions = useToolSessionStore(s => s.subscribeToSessionToolSessions);

  const workspaceItems = useItemsStore(s => s.items);

  const session = useMemo(
    () => sessions.find(s => s.id === sessionId),
    [sessions, sessionId]
  );

  // Prefer the collecting/most recent tool session — the first store match can
  // be a finalized earlier run, which pins the count to stale data.
  const activeToolSession = useMemo(() => {
    const forSession = toolSessions
      .filter(ts => ts.sessionId === sessionId)
      .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
    return forSession.find(ts => ts.status === 'collecting') ?? forSession[0];
  }, [toolSessions, sessionId]);

  const submittedIds = useMemo(
    () => new Set(
      participantResponses
        .filter(r => r.toolSessionId === activeToolSession?.id && r.isComplete)
        .map(r => r.participantId)
    ),
    [participantResponses, activeToolSession]
  );

  const participantUsers = presentUsers.filter(u => u.role !== 'observer');
  const total = participantUsers.length || (session?.participantIds?.length ?? 0);
  const joinUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${joinPathFor(sessionId ?? '')}`;
  const submitted = submittedIds.size;
  const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;

  useEffect(() => {
    if (!sessionId) return;
    void fetchSessionById(sessionId);
    // The TV is a passive viewer on its own device: without subscribing to the
    // session's tool sessions its store is empty and the submitted count sits
    // at zero forever (owner report 2026-07-09).
    void subscribeToSessionToolSessions(sessionId);
  }, [sessionId, fetchSessionById, subscribeToSessionToolSessions]);

  // Join presence as observer
  useEffect(() => {
    if (!sessionId || !userId) return;
    void joinPresence(sessionId, userId, userName, 'observer');
    return () => { void leavePresence(); };
  }, [sessionId, userId, userName, joinPresence, leavePresence]);

  // The result to reveal: names, never ids (the activity's own list names typed
  // options; workspace items fall back to the items store), and tied items
  // share a rank (S-5).
  const revealItems: RevealItem[] = useMemo(() => {
    const output = (session as { rankingOutput?: { rankedItemIds?: string[]; scores?: Record<string, { total?: number }>; ties?: unknown } } | undefined)?.rankingOutput;
    if (!output?.rankedItemIds) return [];
    const ranks = sharedRanks(output.rankedItemIds, readTies(output.ties));
    const activityItems = (activeToolSession?.items ?? []) as Array<{ id: string; name?: string; sourceId?: string }>;
    const nameOf = (id: string) =>
      activityItems.find(i => i.id === id || i.sourceId === id)?.name
      ?? workspaceItems.find(i => i.id === id)?.title
      ?? id;
    return output.rankedItemIds.map((id, idx) => {
      const scoreObj = output.scores?.[id];
      return {
        rank: ranks[id]?.rank ?? idx + 1,
        title: nameOf(id),
        score: typeof scoreObj?.total === 'number' ? scoreObj.total : 0,
      };
    });
  }, [session, activeToolSession, workspaceItems]);

  const reveal = useSessionReveal(sessionId, revealItems.length);
  // S-3: names hidden means the Board shows counts; S-2: the host's timer.
  const { rules: roomRules } = useSessionRoomRules(sessionId);
  const hideNames = namesHidden(roomRules, 'room');
  const roomTimer = useRoomTimer(sessionId, activeToolSession?.status === 'collecting' ? activeToolSession.id : null);
  const tvState: TVState = reveal.phase === 'idle' ? 'collecting' : reveal.phase;
  const { countdown, revealedUpTo } = reveal;

  // A reveal starts after the host closes; the TV fetched the session before
  // that, so read it again for the saved result.
  useEffect(() => {
    if (sessionId && reveal.phase !== 'idle' && !revealItems.length) void fetchSessionById(sessionId);
  }, [sessionId, reveal.phase, revealItems.length, fetchSessionById]);

  // Keyboard: Escape to exit
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate(-1);
      if (e.key === 'f' || e.key === 'F') {
        if (!document.fullscreenElement) void document.documentElement.requestFullscreen();
        else void document.exitFullscreen();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate]);

  const BG = '#0f1117';
  const SURFACE = '#1a1d26';
  const BORDER = '#252a3a';
  const TEXT_PRI = '#f1f5f9';
  const TEXT_SEC = '#94a3b8';
  const TEXT_DIM = '#475569';
  const ACCENT = '#d4a017';

  // COUNTDOWN
  if (tvState === 'countdown') {
    return (
      <div style={{ background: BG, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '1rem', color: TEXT_DIM, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 32 }}>
          Revealing results in
        </div>
        <div
          key={countdown}
          style={{
            fontSize: '20vw',
            fontWeight: 800,
            color: ACCENT,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {countdown > 0 ? countdown : '…'}
        </div>
      </div>
    );
  }

  // REVEALING / COMPLETE
  if (tvState === 'revealing' || tvState === 'complete') {
    const visible = revealItems.slice(0, Math.min(revealedUpTo, revealItems.length));
    const maxScore = Math.max(...revealItems.map(i => i.score), 1);
    return (
      <div style={{ background: BG, minHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '2rem 3rem 1rem', flexShrink: 0, borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: '0.7rem', color: TEXT_DIM, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Results · not published yet</div>
          <div style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', fontWeight: 800, color: TEXT_PRI }}>{session?.name ?? 'Session'}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 3rem 3rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map((item, idx) => {
            const barPct = (item.score / maxScore) * 100;
            const isTop = idx === 0;
            return (
              <div key={`${item.rank}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '1rem 0', borderBottom: `1px solid ${BORDER}` }}>
                <div style={{ width: '4rem', textAlign: 'right', fontSize: isTop ? '2.5rem' : '1.4rem', fontWeight: 800, color: isTop ? ACCENT : TEXT_DIM, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {formatRank(item.rank, revealItems.filter((other) => other.rank === item.rank).length > 1)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: isTop ? 'clamp(1.6rem,2.6vw,2.2rem)' : 'clamp(1.1rem,2vw,1.6rem)', fontWeight: isTop ? 800 : 500, color: isTop ? TEXT_PRI : TEXT_SEC, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 8 }}>
                    {item.title}
                  </div>
                  <div style={{ height: 4, background: BORDER, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${barPct}%`, background: isTop ? ACCENT : '#6366f1', borderRadius: 2, transition: 'width 0.7s ease-out', opacity: isTop ? 1 : 0.6 }} />
                  </div>
                </div>
                <div style={{ width: '5rem', textAlign: 'right', fontSize: isTop ? '1.4rem' : '1rem', fontWeight: 700, color: isTop ? ACCENT : TEXT_DIM, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {item.score.toFixed(1)}
                </div>
              </div>
            );
          })}
          {tvState === 'complete' && (
            <div style={{ textAlign: 'center', padding: '2rem', fontSize: '1rem', color: TEXT_DIM }}>
              ✓ All results revealed
            </div>
          )}
        </div>
      </div>
    );
  }

  // COLLECTING (default)
  const allDone = roomIsAllIn(submitted, total);
  return (
    <div style={{ background: BG, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '2rem 3rem 1.5rem', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <div style={{ fontSize: '0.7rem', color: TEXT_DIM, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>
          Live Session
        </div>
        <div style={{ fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', fontWeight: 800, color: TEXT_PRI }}>
          {session?.name ?? 'Session in progress…'}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'row', padding: '2.5rem 3rem', gap: '2.5rem', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Submission progress */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: 'clamp(3rem, 7vw, 6rem)', fontWeight: 900, color: allDone ? '#34d399' : ACCENT, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {submitted}
            </div>
            <div style={{ fontSize: 'clamp(1.2rem, 2.5vw, 2rem)', color: TEXT_DIM, fontWeight: 500 }}>
              of {total} submitted
            </div>
            {allDone && (
              <div style={{ fontSize: '1rem', color: '#34d399', fontWeight: 700, marginLeft: '1rem' }}>
                ✓ All in — ready to reveal
              </div>
            )}
          </div>
          <div style={{ height: 12, background: BORDER, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: allDone ? '#34d399' : ACCENT, borderRadius: 6, transition: 'width 0.5s ease-out' }} />
          </div>
        </div>

        {roomTimer.endsAt !== null && (
          <div style={{ fontSize: '1.4rem' }}>
            <RoomCountdown
              endsAt={roomTimer.endsAt}
              atZeroLabel={roomTimer.atZero === 'lock' ? 'Time is up · answers are locked' : 'Time is up'}
              className="!text-2xl !px-5 !py-2"
            />
          </div>
        )}

        {hideNames && participantUsers.length > 0 && (
          <div style={{ fontSize: '1.1rem', color: TEXT_SEC }} data-testid="tv-roster-counts">
            {participantUsers.length} in the room · names are hidden in this session
          </div>
        )}

        {/* Avatar grid */}
        {!hideNames && participantUsers.length > 0 && (
          <div>
            <div style={{ fontSize: '0.7rem', color: TEXT_DIM, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1rem' }}>
              In the room
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              {participantUsers.map(u => {
                const done = submittedIds.has(u.userId);
                return (
                  <div
                    key={u.userId}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px', borderRadius: 10,
                      border: `1.5px solid ${done ? '#34d399' : BORDER}`,
                      background: done ? 'rgba(52,211,153,0.06)' : SURFACE,
                    }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: done ? '#34d399' : '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, color: '#fff' }}>
                      {done ? '✓' : initials(u.userName)}
                    </div>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: done ? '#34d399' : TEXT_PRI }}>{u.userName}</div>
                      <div style={{ fontSize: '0.7rem', color: TEXT_DIM }}>{done ? 'Submitted' : (u.currentActivity ?? 'ranking…')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Waiting for facilitator hint */}
        {allDone && (
          <div style={{ fontSize: '1.1rem', color: TEXT_DIM, fontStyle: 'italic' }}>
            The facilitator will reveal results shortly…
          </div>
        )}
        </div>

        {/* Join: phones in the room scan this; the address is beside it for anyone typing.
            It stays up for as long as the room collects: "all in" counts the people
            present, and a late arrival still needs the code. */}
        {sessionId && (
          <aside
            data-testid="tv-join-panel"
            style={{
              flexShrink: 0, width: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem',
              padding: '1.5rem', border: `1px solid ${BORDER}`, borderRadius: 12, background: SURFACE, alignSelf: 'flex-start',
            }}
          >
            <div style={{ fontSize: '0.7rem', color: TEXT_DIM, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              Join on your phone
            </div>
            <JoinQr url={joinUrl} size={220} />
            <div style={{ fontSize: '0.8rem', color: TEXT_PRI, wordBreak: 'break-all', textAlign: 'center', fontFamily: 'JetBrains Mono, monospace' }}>
              {joinUrl.replace(/^https?:\/\//, '')}
            </div>
          </aside>
        )}
      </div>

      {/* Footer */}
      <div style={{ height: 40, borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', padding: '0 3rem', flexShrink: 0 }}>
        <span style={{ fontSize: '0.65rem', color: TEXT_DIM, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Priorities.ai · Live Session · Esc to exit · F for fullscreen
        </span>
      </div>
    </div>
  );
}
